import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Stethoscope, 
  Clock, 
  DollarSign,
  Search,
  Filter,
  Package,
  CheckCircle,
  AlertCircle,
  Video,
  Building
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';

const SERVICE_CATEGORIES = [
  { value: 'consultation', label: 'Consultation', icon: '💬' },
  { value: 'procedure', label: 'Procedure', icon: '🏥' },
  { value: 'lab_test', label: 'Lab Test', icon: '🔬' },
  { value: 'imaging', label: 'Imaging', icon: '📷' },
  { value: 'therapy', label: 'Therapy', icon: '🧘' },
  { value: 'preventive', label: 'Preventive', icon: '🛡️' },
  { value: 'specialty', label: 'Specialty', icon: '⭐' },
  { value: 'other', label: 'Other', icon: '📋' }
];

const APPOINTMENT_MODES = [
  { value: 'in_person', label: 'In-Person', icon: Building },
  { value: 'telemedicine', label: 'Telemedicine', icon: Video },
  { value: 'both', label: 'Both', icon: CheckCircle }
];

const ServicesManagement = () => {
  const [services, setServices] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [showPlanServiceModal, setShowPlanServiceModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planServices, setPlanServices] = useState([]);
  const [addServiceForm, setAddServiceForm] = useState({
    service_id: '',
    quantity_per_period: -1,
    period_type: 'membership',
    is_unlimited: true
  });
  const [editingPlanService, setEditingPlanService] = useState(null);
  const [editServiceForm, setEditServiceForm] = useState({
    quantity_per_period: 1,
    period_type: 'membership',
    is_unlimited: false
  });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'consultation',
    duration_minutes: 30,
    appointment_mode: 'both',
    base_price: 0,
    requires_provider: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, plansRes] = await Promise.all([
        axios.get(`${API}/clinic/services`),
        axios.get(`${API}/plans`)
      ]);
      setServices(servicesRes.data);
      setPlans(plansRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error loading services');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingService) {
        await axios.put(`${API}/clinic/services/${editingService.id}`, formData);
        toast.success('Service updated successfully');
      } else {
        await axios.post(`${API}/clinic/services`, formData);
        toast.success('Service created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving service');
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description || '',
      category: service.category,
      duration_minutes: service.duration_minutes,
      appointment_mode: service.appointment_mode,
      base_price: service.base_price,
      requires_provider: service.requires_provider
    });
    setShowModal(true);
  };

  const handleDelete = async (serviceId) => {
    if (!confirm('Are you sure you want to deactivate this service?')) return;
    try {
      await axios.delete(`${API}/clinic/services/${serviceId}`);
      toast.success('Service deactivated');
      fetchData();
    } catch (error) {
      toast.error('Error deactivating service');
    }
  };

  const resetForm = () => {
    setEditingService(null);
    setFormData({
      name: '',
      description: '',
      category: 'consultation',
      duration_minutes: 30,
      appointment_mode: 'both',
      base_price: 0,
      requires_provider: true
    });
  };

  const fetchPlanServices = async (planId) => {
    try {
      const response = await axios.get(`${API}/clinic/services/plan/${planId}`);
      setPlanServices(response.data);
    } catch (error) {
      console.error('Error fetching plan services:', error);
      setPlanServices([]);
    }
  };

  const handleOpenPlanServices = async (plan) => {
    setSelectedPlan(plan);
    await fetchPlanServices(plan.id);
    setShowPlanServiceModal(true);
  };

  const handleAddServiceToPlan = async (serviceId, quantity, periodType) => {
    try {
      await axios.post(`${API}/clinic/services/plan/${selectedPlan.id}`, {
        service_id: serviceId,
        quantity_per_period: quantity,
        period_type: periodType,
        discount_percentage: 100
      });
      toast.success('Service added to plan');
      fetchPlanServices(selectedPlan.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error adding service');
    }
  };

  const handleRemoveServiceFromPlan = async (planServiceId) => {
    try {
      await axios.delete(`${API}/clinic/services/plan/${selectedPlan.id}/${planServiceId}`);
      toast.success('Service removed from plan');
      fetchPlanServices(selectedPlan.id);
    } catch (error) {
      toast.error('Error removing service');
    }
  };

  const handleEditPlanService = (planService) => {
    setEditingPlanService(planService);
    const isUnlimited = planService.quantity_per_period === -1;
    setEditServiceForm({
      quantity_per_period: isUnlimited ? 1 : planService.quantity_per_period,
      period_type: planService.period_type || 'membership',
      is_unlimited: isUnlimited
    });
  };

  const handleUpdatePlanService = async () => {
    if (!editingPlanService) return;
    
    try {
      await axios.put(
        `${API}/clinic/services/plan/${selectedPlan.id}/${editingPlanService.id}`,
        {
          quantity_per_period: editServiceForm.is_unlimited ? -1 : editServiceForm.quantity_per_period,
          period_type: editServiceForm.period_type
        }
      );
      toast.success('Service quantity updated');
      setEditingPlanService(null);
      fetchPlanServices(selectedPlan.id);
    } catch (error) {
      toast.error('Error updating service');
    }
  };

  const filteredServices = services.filter(service => {
    const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (service.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || service.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getCategoryInfo = (category) => {
    return SERVICE_CATEGORIES.find(c => c.value === category) || SERVICE_CATEGORIES[7];
  };

  const getModeInfo = (mode) => {
    return APPOINTMENT_MODES.find(m => m.value === mode) || APPOINTMENT_MODES[2];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Services Management</h1>
          <p className="text-gray-600 mt-1">Manage the services offered by your clinic</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-teal-600 hover:bg-teal-700">
          <Plus className="h-4 w-4 mr-2" />
          New Service
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Services</p>
                <p className="text-2xl font-bold text-gray-900">{services.length}</p>
              </div>
              <Stethoscope className="h-8 w-8 text-teal-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Consultations</p>
                <p className="text-2xl font-bold text-blue-600">
                  {services.filter(s => s.category === 'consultation').length}
                </p>
              </div>
              <span className="text-3xl">💬</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Procedures</p>
                <p className="text-2xl font-bold text-purple-600">
                  {services.filter(s => s.category === 'procedure').length}
                </p>
              </div>
              <span className="text-3xl">🏥</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Telemedicina</p>
                <p className="text-2xl font-bold text-green-600">
                  {services.filter(s => s.appointment_mode !== 'in_person').length}
                </p>
              </div>
              <Video className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="services" className="space-y-4">
        <TabsList>
          <TabsTrigger value="services">
            <Stethoscope className="h-4 w-4 mr-2" />
            Services
          </TabsTrigger>
          <TabsTrigger value="plans">
            <Package className="h-4 w-4 mr-2" />
            Services by Plan
          </TabsTrigger>
        </TabsList>

        <TabsContent value="services">
          {/* Search and Filter */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search services..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-full md:w-48">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {SERVICE_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.icon} {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Services Table */}
          <Card>
            <CardContent className="pt-6">
              {filteredServices.length === 0 ? (
                <div className="text-center py-12">
                  <Stethoscope className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No services found</p>
                  <Button onClick={() => { resetForm(); setShowModal(true); }} variant="link" className="mt-2">
                    Create the first service
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Service</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Mode</TableHead>
                      <TableHead>Base Price</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredServices.map((service) => {
                      const category = getCategoryInfo(service.category);
                      const mode = getModeInfo(service.appointment_mode);
                      const ModeIcon = mode.icon;
                      
                      return (
                        <TableRow key={service.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-gray-900">{service.name}</p>
                              {service.description && (
                                <p className="text-sm text-gray-500 truncate max-w-xs">{service.description}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="flex items-center gap-1 w-fit">
                              <span>{category.icon}</span>
                              {category.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-gray-600">
                              <Clock className="h-4 w-4" />
                              {service.duration_minutes} min
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                              <ModeIcon className="h-3 w-3" />
                              {mode.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium text-gray-900">
                              ${service.base_price.toFixed(2)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge variant={service.is_active ? 'default' : 'secondary'}>
                              {service.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="ghost" size="sm" onClick={() => handleEdit(service)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(service.id)} className="text-red-600 hover:text-red-700">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="plans">
          <Card>
            <CardHeader>
              <CardTitle>Services by Membership Plan</CardTitle>
              <CardDescription>Define which services each plan includes and their limits</CardDescription>
            </CardHeader>
            <CardContent>
              {plans.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No membership plans</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {plans.map((plan) => (
                    <Card key={plan.id} className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleOpenPlanServices(plan)}>
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="font-semibold text-lg text-gray-900">{plan.name}</h3>
                            <p className="text-sm text-gray-500">${plan.price}/{plan.billing_period}</p>
                          </div>
                          <Badge variant={plan.is_active ? 'default' : 'secondary'}>
                            {plan.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        <Button variant="outline" className="w-full">
                          <Package className="h-4 w-4 mr-2" />
                          Manage Services
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Service Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingService ? 'Edit Service' : 'New Service'}</DialogTitle>
            <DialogDescription>
              {editingService ? 'Modify the service details' : 'Add a new service to your clinic'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Service Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., General Consultation"
                  required
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the service..."
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="category">Category *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.icon} {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="duration">Duration (minutes) *</Label>
                <Input
                  id="duration"
                  type="number"
                  min="5"
                  max="480"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="mode">Mode *</Label>
                <Select value={formData.appointment_mode} onValueChange={(v) => setFormData({ ...formData, appointment_mode: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {APPOINTMENT_MODES.map(mode => (
                      <SelectItem key={mode.value} value={mode.value}>
                        {mode.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="price">Base Price (non-members)</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    id="price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.base_price}
                    onChange={(e) => setFormData({ ...formData, base_price: parseFloat(e.target.value) })}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="col-span-2 flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label htmlFor="requires_provider">Requires Provider</Label>
                  <p className="text-sm text-gray-500">Does this service need a doctor/specialist assigned?</p>
                </div>
                <Switch
                  id="requires_provider"
                  checked={formData.requires_provider}
                  onCheckedChange={(v) => setFormData({ ...formData, requires_provider: v })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                {editingService ? 'Save Changes' : 'Create Service'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Plan Services Modal */}
      <Dialog open={showPlanServiceModal} onOpenChange={setShowPlanServiceModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Services for {selectedPlan?.name}</DialogTitle>
            <DialogDescription>
              Manage services included in this membership plan
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Current Services */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Included Services</h4>
              {planServices.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">No services assigned to this plan</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {planServices.map((ps) => (
                    <div key={ps.id} className="p-3 bg-gray-50 rounded-lg">
                      {editingPlanService?.id === ps.id ? (
                        // Edit Mode
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <p className="font-medium">{ps.service?.name}</p>
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" onClick={() => setEditingPlanService(null)}>
                                Cancel
                              </Button>
                              <Button size="sm" onClick={handleUpdatePlanService} className="bg-teal-600 hover:bg-teal-700">
                                Save
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={editServiceForm.is_unlimited}
                                onCheckedChange={(v) => setEditServiceForm({
                                  ...editServiceForm,
                                  is_unlimited: v,
                                  quantity_per_period: v ? -1 : 1
                                })}
                              />
                              <Label className="text-sm">Unlimited</Label>
                            </div>
                            {!editServiceForm.is_unlimited && (
                              <>
                                <Input
                                  type="number"
                                  min="1"
                                  value={editServiceForm.quantity_per_period}
                                  onChange={(e) => setEditServiceForm({
                                    ...editServiceForm,
                                    quantity_per_period: parseInt(e.target.value) || 1
                                  })}
                                  placeholder="Quantity"
                                  className="h-9"
                                />
                              </>
                            )}
                          </div>
                          {!editServiceForm.is_unlimited && (
                            <Select 
                              value={editServiceForm.period_type} 
                              onValueChange={(v) => setEditServiceForm({...editServiceForm, period_type: v})}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="membership">Per Membership</SelectItem>
                                <SelectItem value="month">Per Month</SelectItem>
                                <SelectItem value="year">Per Year</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      ) : (
                        // View Mode
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{ps.service?.name}</p>
                            <p className="text-sm text-gray-500">
                              {ps.quantity_per_period === -1 ? 'Unlimited' : `${ps.quantity_per_period} per ${ps.period_type}`}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEditPlanService(ps)} className="text-blue-600 hover:text-blue-700">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleRemoveServiceFromPlan(ps.id)} className="text-red-600 hover:text-red-700">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add Service */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Add Service to Plan</h4>
              <div className="space-y-4">
                {/* Service Selection */}
                <div>
                  <Label>Select Service</Label>
                  <Select 
                    value={addServiceForm.service_id} 
                    onValueChange={(v) => setAddServiceForm({...addServiceForm, service_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a service..." />
                    </SelectTrigger>
                    <SelectContent>
                      {services.filter(s => !planServices.find(ps => ps.service_id === s.id)).map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {getCategoryInfo(service.category).icon} {service.name} ({service.duration_minutes} min)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Quantity Configuration */}
                <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Unlimited Usage</Label>
                      <p className="text-xs text-gray-500">Allow unlimited use of this service</p>
                    </div>
                    <Switch
                      checked={addServiceForm.is_unlimited}
                      onCheckedChange={(v) => setAddServiceForm({
                        ...addServiceForm, 
                        is_unlimited: v,
                        quantity_per_period: v ? -1 : 1
                      })}
                    />
                  </div>

                  {!addServiceForm.is_unlimited && (
                    <>
                      <div>
                        <Label>Quantity Allowed</Label>
                        <Input
                          type="number"
                          min="1"
                          value={addServiceForm.quantity_per_period}
                          onChange={(e) => setAddServiceForm({
                            ...addServiceForm, 
                            quantity_per_period: parseInt(e.target.value) || 1
                          })}
                          placeholder="e.g., 5"
                        />
                      </div>
                      <div>
                        <Label>Period</Label>
                        <Select 
                          value={addServiceForm.period_type} 
                          onValueChange={(v) => setAddServiceForm({...addServiceForm, period_type: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="membership">Per Membership</SelectItem>
                            <SelectItem value="month">Per Month</SelectItem>
                            <SelectItem value="year">Per Year</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500 mt-1">
                          {addServiceForm.period_type === 'membership' 
                            ? 'Usage resets when membership is renewed'
                            : addServiceForm.period_type === 'month'
                            ? 'Usage resets at the start of each month'
                            : 'Usage resets at the start of each year'
                          }
                        </p>
                      </div>
                    </>
                  )}
                </div>

                <Button 
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  disabled={!addServiceForm.service_id}
                  onClick={() => {
                    handleAddServiceToPlan(
                      addServiceForm.service_id, 
                      addServiceForm.is_unlimited ? -1 : addServiceForm.quantity_per_period, 
                      addServiceForm.period_type
                    );
                    setAddServiceForm({
                      service_id: '',
                      quantity_per_period: -1,
                      period_type: 'membership',
                      is_unlimited: true
                    });
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Service to Plan
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicesManagement;
